import { User } from '@supabase/supabase-js';
import React, { FC, useEffect, useState } from 'react';

import { Banner } from '~/components/banner';
import { Modal } from '~/components/modal';
import { Navbar } from '~/components/navbar';

import { PageWrapper } from './PageWrapper/pageWrapper';

type Props = {
    children: React.ReactElement | React.ReactElement[];
    isLive?: boolean;
    user: User | null;
};

type CookiePreference = {
    cookieName: 'theme-preference';
    isChecked: boolean;
};

const applyCookieSettings = (prefs: CookiePreference[]): void => {
    localStorage.setItem('cookies-chosen', 'true');
    for (const pref of prefs) {
        switch (pref.cookieName) {
            case 'theme-preference': {
                localStorage.setItem(
                    'theme-preference-cookie-allowed',
                    `${pref.isChecked}`
                );
                break;
            }
        }
    }
};

export const Layout: FC<Props> = ({ children, isLive = true, user }: Props) => {
    const [cookiePreferences, setCookiePreferences] = useState<
        CookiePreference[]
    >([
        {
            cookieName: 'theme-preference',
            isChecked: true,
        },
    ]);
    const [cookieModalOpen, setCookieModalOpen] = useState<boolean>(false);
    const [cookieBannerVisible, setCookieBannerVisible] = useState<boolean>();

    useEffect(() => {
        if (localStorage.getItem('cookies-chosen') !== 'true') {
            setCookieBannerVisible(true);
        }
    }, []);

    return (
        <>
            <Navbar isLive={isLive} user={user} />
            <PageWrapper>
                <div className="md:mx-6">{children}</div>
                <Modal
                    closeText="Save and Close"
                    content={[
                        <div
                            className="form-control"
                            key="strictly-necessary-checkbox"
                        >
                            <div
                                className="tooltip"
                                data-tip="These cookies are necessary to ensure that the site remains functional."
                            >
                                <label className="label cursor-pointer">
                                    <span className="label-text">
                                        Strictly Necessary Cookies
                                    </span>
                                    <input
                                        className="checkbox"
                                        defaultChecked
                                        disabled
                                        type="checkbox"
                                    />
                                </label>
                            </div>
                        </div>,
                        <div
                            className="form-control"
                            key="theme-preference-checkbox"
                        >
                            <div
                                className="tooltip"
                                data-tip="These cookies are necessary to ensure that your choice of theme for the site (such as light / dark mode) remains functional."
                            >
                                <label className="label cursor-pointer">
                                    <span className="label-text">
                                        Theme Preference Cookies
                                    </span>
                                    <input
                                        className="checkbox"
                                        onChange={(event) => {
                                            const index =
                                                cookiePreferences.findIndex(
                                                    (value) =>
                                                        value.cookieName ===
                                                        'theme-preference'
                                                );

                                            setCookiePreferences(
                                                cookiePreferences.map(
                                                    (value, idx) =>
                                                        idx === index
                                                            ? {
                                                                  cookieName:
                                                                      'theme-preference',
                                                                  isChecked:
                                                                      event
                                                                          .currentTarget
                                                                          .checked,
                                                              }
                                                            : value
                                                )
                                            );
                                        }}
                                        type="checkbox"
                                    />
                                </label>
                            </div>
                        </div>,
                    ]}
                    heading="Customize Cookie Settings"
                    id="cookie-modal"
                    onClose={() => {
                        applyCookieSettings(cookiePreferences);
                        setCookieBannerVisible(false);
                        setCookieModalOpen(false);
                    }}
                    open={cookieModalOpen}
                />
                {cookieBannerVisible && (
                    <Banner
                        buttons={[
                            <button
                                className="btn btn-primary"
                                key="accept"
                                onClick={() => {
                                    applyCookieSettings(cookiePreferences);
                                    setCookieBannerVisible(false);
                                }}
                            >
                                Accept
                            </button>,
                            <button
                                className="btn btn-primary"
                                key="decline"
                                onClick={() => {
                                    const values = cookiePreferences.map(
                                        (pref) => {
                                            return {
                                                ...pref,
                                                isChecked: false,
                                            };
                                        }
                                    );
                                    applyCookieSettings(values);
                                    setCookieBannerVisible(false);
                                }}
                            >
                                Deny
                            </button>,
                            <button
                                className="btn btn-primary"
                                key="customize"
                                onClick={() => {
                                    setCookieModalOpen(true);
                                }}
                            >
                                Customize
                            </button>,
                        ]}
                        content="This site uses cookies to personalize your experience."
                        heading="Cookies"
                    />
                )}
            </PageWrapper>
        </>
    );
};
